import { Callback } from "game-common/models"
import { Circle, Graphics, LINE_CAP } from "pixi.js"

import { closeControlRadius, controlHitRect, getUiMetrics, ResponsiveHitRect } from "./ui_metrics"
import { HUD_PANEL_FILL, HUD_PANEL_RIM, HUD_PANEL_TEXT, HUD_PANEL_TEXT_MUTED } from "./ui_tokens"

// Shared circular close button for dialogs/modals. It replaces the bare "x" glyph the Dialog used to
// drop in the top-right corner: a dark disc with a light rim and a stroked X, drawn CENTERED ON ITS
// OWN ORIGIN so a caller can pin it straight onto a panel corner and let it hang over the edge.
//
// Compatibility export for fixed panel-disc siblings. New instances read live input metrics.
export const CLOSE_BUTTON_RADIUS = closeControlRadius()

const RIM = 0x8d97ad
const RIM_HOVER = 0xd9a441 // gold, matching the inventory/crafting panel accent
const FILL = 0x1b2030
const FILL_HOVER = 0x2f3648
const GLYPH = 0xe6e9f0
const GLYPH_HOVER = 0xffe6b0

/**
 * Which family a disc belongs to.
 *
 * The constants above are the inventory/crafting panels' -- cool navy and slate, which is what those
 * panels are. The ember HUD's floating panels are timber and bronze (ui_tokens HUD_PANEL_*), and a
 * disc hung off one of THOSE corners in navy is the only cool thing left on a warm plate. Rather
 * than warm every disc in the game and put the same mismatch on the crafting screen, a caller says
 * which family it is in.
 */
export type PanelDiscTheme = "slate" | "ember"
const DISC = {
    slate: { fill: FILL, fillHover: FILL_HOVER, glyph: GLYPH, glyphHover: GLYPH_HOVER, rim: RIM },
    ember: {
        fill: HUD_PANEL_FILL, fillHover: 0x2f2318, glyph: HUD_PANEL_TEXT_MUTED,
        glyphHover: HUD_PANEL_TEXT, rim: HUD_PANEL_RIM,
    },
} as const

export interface CloseButtonProps {
    radius?: number
    // Which panel family the disc hangs off (see PanelDiscTheme). Slate unless the panel says.
    theme?: PanelDiscTheme
    onClick?: Callback
    hitPadding?: number
}

// The disc every panel-corner control sits on: a fake drop shadow, a dark fill and a light rim that
// goes gold on hover. Exported because the ember minimap hangs a SECOND control off its corner (the
// size cycle) and the two must be the same object with different glyphs -- a resize button drawn from
// its own constants would drift away from the close button beside it on the first restyle.
//
// Leaves the Graphics with no line style set, ready for the caller's glyph.
export const drawPanelDisc = (g: Graphics, radius: number, hover: boolean, theme: PanelDiscTheme = "slate") => {
    g.clear()

    // Fake drop shadow -- an offset dark disc behind, rather than a filter (see the budget hardware
    // target: filters cost a render pass per instance).
    g.lineStyle(0)
    g.beginFill(0x000000, 0.35)
    g.drawCircle(0, 1.5, radius)
    g.endFill()

    const tones = DISC[theme]
    g.lineStyle({ width: 2, color: hover ? RIM_HOVER : tones.rim, alpha: 1 })
    g.beginFill(hover ? tones.fillHover : tones.fill, 0.98)
    g.drawCircle(0, 0, radius)
    g.endFill()
    g.lineStyle(0)
}

// The colour a corner control's glyph is stroked in, so a sibling control's glyph matches the X's.
export const panelDiscGlyphColor = (hover: boolean, theme: PanelDiscTheme = "slate"): number =>
    hover ? DISC[theme].glyphHover : DISC[theme].glyph

export class CloseButton extends Graphics {
    private radius: number
    private fixedRadius?: number
    private hovered = false
    private hitPadding: number
    private theme: PanelDiscTheme

    constructor({ radius, onClick, hitPadding, theme = "slate" }: CloseButtonProps = {}) {
        super()
        this.theme = theme
        this.fixedRadius = radius
        this.radius = radius ?? closeControlRadius()
        this.hitPadding = hitPadding ?? (radius === undefined ? Infinity : 3)

        this.draw(false)

        this.interactive = true
        this.cursor = "pointer"
        // An explicit circular hit area, a couple of px proud of the rim: the disc is small and this
        // is the one control every dialog is closed by, so it should be forgiving to hit (and to tap).
        this.updateHitArea()


        this.on("mouseover", () => this.draw(true))
        this.on("mouseout", () => this.draw(false))
        if (onClick) {
            this.on("click", onClick)
            this.on("tap", onClick)
        }
    }

    private draw = (hover: boolean) => {
        this.hovered = hover
        const r = this.radius
        drawPanelDisc(this, r, hover, this.theme)

        const arm = r * 0.4
        this.lineStyle({
            width: Math.max(2, r * 0.19),
            color: panelDiscGlyphColor(hover, this.theme),
            alpha: 1,
            cap: LINE_CAP.ROUND,
        })
        this.moveTo(-arm, -arm)
        this.lineTo(arm, arm)
        this.moveTo(arm, -arm)
        this.lineTo(-arm, arm)
        this.lineStyle(0)
    }

    getRadius = () => this.radius

    getHitSize = () => {
        if (this.hitArea instanceof ResponsiveHitRect) {
            this.hitArea.refresh()
            return { width: this.hitArea.width, height: this.hitArea.height }
        }
        return { width: (this.radius + 3) * 2, height: (this.radius + 3) * 2 }
    }

    private updateHitArea = () => {
        this.hitArea = getUiMetrics().touch ? new ResponsiveHitRect(() => {
            const rect = controlHitRect(this, this.radius * 2, this.radius * 2, this.hitPadding)
            rect.x -= this.radius
            rect.y -= this.radius
            return rect
        }) : new Circle(0, 0, this.radius + 3)
    }

    refreshMetrics = () => {
        this.radius = this.fixedRadius ?? closeControlRadius()
        this.updateHitArea()
        this.draw(this.hovered)
    }
}
